import React, { useEffect, useMemo, useRef, useState } from "react";
import HospitalCustomPaginaion from "../utils/HospitalPaginationUtil";
import { ReportServices } from "../services/ReportServices";
import { HospitalServices } from "../services/HospitalServices";
import {
  getChartDateRangeForYmdStrings,
  getChartDateRangeForTimeframe,
} from "./doctorReportDateRange";
import informationIcon from "../assets/information-icon.svg";

/** Maps DoctorPerformanceReport radio values → GET cc-doctor-processed-stage-list query params. */
export function buildCcDoctorProcessedStageListParams({
  page,
  limit,
  caseTypeUi,
  caseStageUi,
  caseSearchKey,
  doctorId,
}) {
  const params = { page, limit };
  const ct = String(caseTypeUi ?? "").trim();
  if (ct && ct !== "All") {
    const map = {
      Daycare: "DAYCARE",
      Conservative: "CONSERVATIVE",
      Surgical: "SURGICAL",
    };
    const v = map[ct];
    if (v) params.caseType = v;
  }
  const cs = String(caseStageUi ?? "").trim();
  if (cs && cs !== "All") {
    const map = {
      Admission: "admission",
      Enhancement: "enhancement",
      Claim: "claim",
    };
    const v = map[cs];
    if (v) params.caseStage = v;
  }
  const csk = String(caseSearchKey ?? "").trim();
  if (csk) params.caseSearchKey = csk;
  const did = doctorId != null ? String(doctorId).trim() : "";
  if (did) params.doctorId = did;
  return params;
}

/**
 * GET cc-doctor-processed-stage-count — omit caseType/caseStage so the response includes every bucket
 * (used for header radio counts; scope with doctor + search only).
 */
export function buildCcDoctorProcessedStageCountParams({
  caseSearchKey,
  doctorId,
}) {
  const params = {};
  const csk = String(caseSearchKey ?? "").trim();
  if (csk) params.caseSearchKey = csk;
  const did = doctorId != null ? String(doctorId).trim() : "";
  if (did) params.doctorId = did;
  return params;
}

const COL_CELL_CLASS =
  "font-lufga font-medium text-sm leading-[18px] text-black";
const REGULAR_CELL_CLASS =
  "font-lufga font-normal text-sm leading-[100%] tracking-[-0.03em]";
const PAGE_LIMIT = 10;

const PROCEDURE_TRUNCATE_LENGTH = 35;

function formatCaseType(raw) {
  if (raw == null || raw === "") return "—";
  const s = String(raw).replace(/_/g, " ").toLowerCase();
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Show only the high-level bucket: Admission | Enhancement | Claim.
 * e.g. "Admission Documents Uploaded" → "Admission"
 */
function simplifyCaseStage(commandCenterStage, hsaStage) {
  const cc = String(commandCenterStage ?? "").toLowerCase();
  const hs = String(hsaStage ?? "").toLowerCase();
  if (
    cc.includes("claim") ||
    hs.includes("claim") ||
    cc.includes("discharge")
  ) {
    return "Claim";
  }
  if (cc.includes("enhancement") || hs.includes("enhancement")) {
    return "Enhancement";
  }
  if (cc.includes("admission") || hs.includes("admission")) {
    return "Admission";
  }
  return "—";
}

/** Time + date lines to match existing grid layout */
function formatGridDateTime(iso) {
  if (iso == null || iso === "") return { time: "—", date: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { time: "—", date: "" };
  return {
    time: d.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }),
    date: d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
  };
}

/** processTime from API — treated as total seconds (TAT). */
function formatDurationSeconds(totalSeconds) {
  const n = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  if (n === 0) return "—";
  const d = Math.floor(n / 86400);
  const h = Math.floor((n % 86400) / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = n % 60;
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (!parts.length) {
    parts.push(`${s}s`);
  } else if (s > 0) {
    parts.push(`${s}s`);
  }
  return parts.join(" ");
}

/** Same resolution pattern as ManageProcedurePage (gethospitals list). */
function resolveHospitalName(hospitalId, hospitalList) {
  if (hospitalId == null || hospitalId === "" || hospitalId === "—") return "—";
  const id = String(hospitalId);
  if (!Array.isArray(hospitalList) || hospitalList.length === 0) return id;
  const hosp = hospitalList.find(
    (h) => String(h?.id ?? "") === id || String(h?.hospitalId ?? "") === id,
  );
  return hosp ? (hosp.name ?? hosp.hospitalName ?? id) : id;
}

/** Table cell: show first `maxWords` words + "…"; tooltip shows full name (never hospital id). */
function getHospitalNameCellDisplay(hospitalName, maxWords = 3) {
  if (hospitalName == null || hospitalName === "" || hospitalName === "—") {
    return { short: "—", title: undefined };
  }
  const s = String(hospitalName).trim();
  if (!s) return { short: "—", title: undefined };
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) {
    return { short: s, title: undefined };
  }
  return {
    short: `${words.slice(0, maxWords).join(" ")}…`,
    title: s,
  };
}

/** Same id → display name map as ManageScheme / ManageProcedureSchemeMapping (`ReportServices.getUsers`). */
function resolveUserDisplayName(userId, usersMap) {
  if (userId == null || userId === "") return null;
  const id = String(userId).trim();
  if (!id) return null;
  const name = usersMap?.[id];
  if (name != null && String(name).trim() !== "") return String(name);
  return id;
}

/**
 * Procedure labels from `case.caseprocedures` (cc-doctor-processed-stage-list).
 * Supports `caseprocedures` or `caseProcedures` if the API varies.
 */
function getCaseProcedureNames(caseObj) {
  const list = caseObj?.caseprocedures ?? caseObj?.caseProcedures;
  if (!Array.isArray(list) || list.length === 0) return [];
  return list
    .map((cp) => {
      const raw = cp?.procedure;
      if (raw == null || String(raw).trim() === "") return null;
      return String(raw).replace(/\s+/g, " ").trim();
    })
    .filter(Boolean);
}

/** Join for tooltips / legacy single string. */
function formatCaseProcedures(caseObj) {
  const parts = getCaseProcedureNames(caseObj);
  if (parts.length === 0) return "—";
  return parts.join("; ");
}

/** Length of `case.caseprocedures` / `case.caseProcedures` array from API. */
function getCaseProceduresCount(caseObj) {
  const list = caseObj?.caseprocedures ?? caseObj?.caseProcedures;
  return Array.isArray(list) ? list.length : 0;
}

/** Match `performedTask.commandCenterStage` to `commandCenterStageAction` keys (exact, then normalized spaces). */
function normalizeStageKey(s) {
  return String(s ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

/** API may return a string (legacy) or `{ action, color }`. */
function parseCommandCenterStageEntry(val) {
  if (val == null) return { label: "—", rowColor: null };
  if (typeof val === "string") {
    const s = val.trim();
    return { label: s !== "" ? s : "—", rowColor: null };
  }
  if (typeof val === "object" && val !== null) {
    const action = val.action;
    const color = val.color;
    const label =
      action != null && String(action).trim() !== ""
        ? String(action).trim()
        : "—";
    const rowColor =
      color != null && String(color).trim() !== ""
        ? String(color).trim()
        : null;
    return { label, rowColor };
  }
  return { label: "—", rowColor: null };
}

const ROW_GRADIENT_EDGE = "rgba(255, 255, 255, 0.6)";
const ROW_GRADIENT_MID_ALPHA = 0.6;

/** Table min width: base + 13 cols + horizontal gaps (`gap-x-2` × 12); Procedures Count column hidden */
const REPORT_GRID_GAP_X_PX = 8;
/** 11 flexible cols + Procedure (172px Figma) + Status */
const REPORT_GRID_TEMPLATE_COLS =
  "repeat(11, minmax(0, 1fr)) minmax(172px, 172px) minmax(0, 1fr)";
const REPORT_TABLE_WIDTH_PX = 1580 - 88 + 12 * REPORT_GRID_GAP_X_PX;
const REPORT_ROW_GAP_PX = 10;
const REPORT_ROW_CELL_PAD = "py-2 px-3 lg:px-4 min-w-0";

/** Parse hex / rgb / rgba into `{ r, g, b }` for building row gradients. */
function parseCssColorToRgb(color) {
  const s = String(color).trim();
  if (s.startsWith("#")) {
    let h = s.slice(1);
    if (h.length === 3) {
      h = h
        .split("")
        .map((c) => c + c)
        .join("");
    }
    if (h.length === 6 || h.length === 8) {
      const n = parseInt(h.slice(0, 6), 16);
      if (!Number.isNaN(n)) {
        return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
      }
    }
  }
  const m = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) {
    return { r: +m[1], g: +m[2], b: +m[3] };
  }
  return null;
}

/**
 * Same shape as `linear-gradient(90deg, white 0%, accent 15%–82%, white 100%)` with 0.6 opacity,
 * using the backend color as the accent band.
 */
function styleForActionRowBackground(backendColor) {
  const rgb = parseCssColorToRgb(backendColor);
  if (!rgb) {
    return { backgroundColor: backendColor, borderRadius: 55 };
  }
  const mid = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${ROW_GRADIENT_MID_ALPHA})`;
  return {
    background: `linear-gradient(90deg, ${ROW_GRADIENT_EDGE} 0%, ${mid} 15%, ${mid} 82%, ${ROW_GRADIENT_EDGE} 100%)`,
    borderRadius: 55,
  };
}

/** Match `performedTask.commandCenterStage` to `commandCenterStageAction` keys; returns action text + row background. */
function resolvePerformedTaskActionMeta(performedTask, actionMap) {
  if (!actionMap || typeof actionMap !== "object") {
    return { label: "—", rowColor: null };
  }
  const raw = performedTask?.commandCenterStage;
  if (raw == null || String(raw).trim() === "") {
    return { label: "—", rowColor: null };
  }
  const key = String(raw);
  if (Object.prototype.hasOwnProperty.call(actionMap, key)) {
    return parseCommandCenterStageEntry(actionMap[key]);
  }
  const norm = normalizeStageKey(key);
  for (const [mapKey, mapVal] of Object.entries(actionMap)) {
    if (normalizeStageKey(mapKey) === norm) {
      return parseCommandCenterStageEntry(mapVal);
    }
  }
  return { label: "—", rowColor: null };
}

/** Parse inner bucket value as hours (numeric string from API). */
function parseHoursValue(val) {
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

/** Match stage name in a ccStageTime inner bucket; returns hours or null. */
function getHoursForStageInBucket(bucket, stageName) {
  if (!bucket || typeof bucket !== "object") return null;
  if (stageName == null || String(stageName).trim() === "") return null;
  const key = String(stageName);
  if (Object.prototype.hasOwnProperty.call(bucket, key)) {
    return parseHoursValue(bucket[key]);
  }
  const norm = normalizeStageKey(key);
  for (const [mapKey, mapVal] of Object.entries(bucket)) {
    if (normalizeStageKey(mapKey) === norm) return parseHoursValue(mapVal);
  }
  return null;
}

/** First numeric hours in a bucket (fallback when key missing), e.g. any "7" in the `null` group. */
function getFirstHoursInBucket(bucket) {
  if (!bucket || typeof bucket !== "object") return null;
  for (const val of Object.values(bucket)) {
    const h = parseHoursValue(val);
    if (h != null) return h;
  }
  return null;
}

function getOffsetHoursFromBucket(bucket, stageName) {
  if (!bucket || typeof bucket !== "object") return null;
  const byStage = getHoursForStageInBucket(bucket, stageName);
  if (byStage != null) return byStage;
  return getFirstHoursInBucket(bucket);
}

/**
 * Hours to add to performedTask.createdAt for Due column.
 * Lookup order: ccStageTime[performedTask.id], then ccStageTime["null"], then first other bucket.
 */
function resolveDueOffsetHours(performedTask, ccStageTime) {
  if (!ccStageTime || typeof ccStageTime !== "object") return null;
  const stage = performedTask?.commandCenterStage;
  const tid = performedTask?.id != null ? String(performedTask.id) : null;

  if (tid && Object.prototype.hasOwnProperty.call(ccStageTime, tid)) {
    const h = getOffsetHoursFromBucket(ccStageTime[tid], stage);
    if (h != null) return h;
  }

  const nullBucket = ccStageTime["null"];
  const hNull = getOffsetHoursFromBucket(nullBucket, stage);
  if (hNull != null) return hNull;

  for (const [key, bucket] of Object.entries(ccStageTime)) {
    if (key === tid || key === "null") continue;
    const h = getOffsetHoursFromBucket(bucket, stage);
    if (h != null) return h;
  }
  return null;
}

/** Due = performedTask.createdAt + resolved hours from ccStageTime (values treated as hours). */
//cc stage time is use as 7 minutes from createdAt
function formatDueFromPerformedTask(performedTask, ccStageTime) {
  const createdRaw = performedTask?.createdAt;
  if (createdRaw == null || createdRaw === "") {
    return { time: "—", date: "" };
  }
  const base = new Date(createdRaw); // initial time and date
  if (Number.isNaN(base.getTime())) return { time: "—", date: "" };

  const mins = resolveDueOffsetHours(performedTask, ccStageTime);
  if (mins == null || !Number.isFinite(mins)) {
    return formatGridDateTime(createdRaw);
  }
  const dueMs = base.getTime() + mins * 60 * 1000;
  return formatGridDateTime(new Date(dueMs).toISOString());
}

function formatAssignedDoctor(item, usersMap) {
  const ad = item?.assignedDoctor;
  const at = item?.assignedTo;

  if (ad != null && typeof ad === "object") {
    const direct = ad.fullName ?? ad.name ?? ad.userName ?? ad.email ?? "";
    if (direct) return String(direct);
    const oid = ad.id ?? ad.userId;
    if (oid != null && oid !== "") {
      const resolved = resolveUserDisplayName(oid, usersMap);
      return resolved != null ? resolved : "—";
    }
    return "—";
  }

  if (ad != null && typeof ad === "string") {
    const s = ad.trim();
    if (!s) return "—";
    const resolved = resolveUserDisplayName(s, usersMap);
    return resolved != null ? resolved : s;
  }

  if (at != null && at !== "") {
    const resolved = resolveUserDisplayName(at, usersMap);
    return resolved != null ? resolved : "—";
  }

  return "—";
}

function mapStageToRow(item, usersMap, commandCenterStageAction, ccStageTime) {
  const c = item.case ?? {};
  const p = c.patient ?? {};
  const start = formatGridDateTime(item.assginDate ?? item.assignDate);
  const end = formatGridDateTime(item.updatedAt);
  const due = formatGridDateTime(item.createdAt);

  const actionMeta = resolvePerformedTaskActionMeta(
    item.performedTask,
    commandCenterStageAction,
  );

  const startDateTime = new Date(item.assginDate ?? item.assignDate);
  const endDateTime = new Date(item.updatedAt);
  const tatSeconds = Math.floor((endDateTime - startDateTime) / 1000);

  return {
    id: item.id,
    patientName: p.patientName ?? "—",
    beneficiaryNo: p.beneficiaryNo ?? "—",
    hospitalId: c.hospitalId != null ? String(c.hospitalId) : "—",
    claimId:
      c.pmjayRegistrationId != null && c.pmjayRegistrationId !== ""
        ? String(c.pmjayRegistrationId)
        : c.pmjayId != null && c.pmjayId !== ""
          ? String(c.pmjayId)
          : "—",
    caseStage: simplifyCaseStage(item.commandCenterStage, item.hsaStage),
    caseType: formatCaseType(c.caseType),
    dueTime: due.time,
    dueDate: due.date,
    startTime: start.time,
    startDate: start.date,
    endTime: end.time,
    endDate: end.date,
    tat: formatDurationSeconds(tatSeconds),
    assigned: formatAssignedDoctor(item, usersMap),
    action: actionMeta.label,
    actionRowColor: actionMeta.rowColor,
    rejectionReason:
      item?.performedTask?.rejectionDetails?.rejectionReason ?? null,
    isRejected:
      item?.performedTask?.commandCenterStage
        ?.toLowerCase()
        .includes("documents rejected") ?? false,
    procedure: formatCaseProcedures(c),
    procedureNames: getCaseProcedureNames(c),
    procedureCount: getCaseProceduresCount(c),
    status: item.commandCenterStageText ?? item.commandCenterStage ?? "—",
  };
}

function truncateProcedureLabel(s) {
  if (s.length <= PROCEDURE_TRUNCATE_LENGTH) return s;
  return `${s.slice(0, PROCEDURE_TRUNCATE_LENGTH)}...`;
}

function ProcedureCell({ procedureNames }) {
  const list = Array.isArray(procedureNames) ? procedureNames : [];
  const [showFull, setShowFull] = useState(false);

  if (list.length === 0) {
    return <span>—</span>;
  }

  const fullJoined = list.join("; ");
  const singleLong =
    list.length === 1 && fullJoined.length > PROCEDURE_TRUNCATE_LENGTH;
  const multiple = list.length > 1;
  const needsToggle = singleLong || multiple;

  if (!needsToggle) {
    return <span title={fullJoined}>{fullJoined}</span>;
  }

  if (showFull) {
    return (
      <div className="inline-block max-w-full align-top text-left">
        {multiple ? (
          <div className="flex flex-col gap-3">
            {list.map((name, i) => (
              <span key={i} className="block pb-0.5 last:pb-0" title={name}>
                {i + 1}. {name}
              </span>
            ))}
          </div>
        ) : (
          <span title={list[0]}>{list[0]}</span>
        )}
        <button
          type="button"
          onClick={() => setShowFull(false)}
          className="font-lufga font-normal text-xs leading-[100%] tracking-[-0.03em] underline text-teal-600 ml-1 bg-transparent border-none cursor-pointer align-baseline"
        >
          showless
        </button>
      </div>
    );
  }

  const collapsedPreview = multiple
    ? truncateProcedureLabel(list[0])
    : truncateProcedureLabel(fullJoined);

  return (
    <div className="inline max-w-full">
      <span title={list[0]}>{collapsedPreview}</span>
      <button
        type="button"
        onClick={() => setShowFull(true)}
        className="font-lufga font-normal text-xs leading-[100%] tracking-[-0.03em] underline text-teal-600 ml-1 bg-transparent border-none cursor-pointer"
      >
        ...showmore
      </button>
    </div>
  );
}

function RejectionInfoIcon({ reason }) {
  const [show, setShow] = useState(false);

  if (!reason) return null;

  return (
    <div
      className="relative inline-flex items-center ml-1"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <img
        src={informationIcon}
        alt="info"
        className="w-4 h-4 cursor-pointer"
      />

      {show && (
        <div
          className="absolute z-50"
          style={{
            width: 216,
            borderRadius: 16,
            padding: 8,
            background: "rgba(255,255,255,1)",
            boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
            bottom: "24px",
            left: "-100px",
          }}
        >
          <span
            style={{
              fontFamily: "Lufga",
              fontWeight: 400,
              fontSize: 10,
              lineHeight: "100%",
              letterSpacing: "-0.03em",
            }}
          >
            Reason: {reason}
          </span>
        </div>
      )}
    </div>
  );
}

export default function ReportView({
  commandCenterStageAction = null,
  ccStageTime = null,
  caseTypeFilter = "All",
  caseStageFilter = "All",
  caseSearchKey = "",
  doctorId = null,
  timeframe,
  chartDateSource,
  customRangeStart,
  customRangeEnd,
  refreshNonce = 0,
}) {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rawStages, setRawStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hospitalList, setHospitalList] = useState([]);
  const [usersMap, setUsersMap] = useState(null);
  const [debouncedCaseSearch, setDebouncedCaseSearch] = useState(() =>
    String(caseSearchKey ?? "").trim(),
  );
  /** `{ patientName, procedures }` when modal open — lists all procedures for that row. */
  const [procedureListModal, setProcedureListModal] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedCaseSearch(String(caseSearchKey ?? "").trim());
    }, 400);
    return () => clearTimeout(t);
  }, [caseSearchKey]);

  useEffect(() => {
    if (!procedureListModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") setProcedureListModal(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [procedureListModal]);

  const filterKey = [
    caseTypeFilter,
    caseStageFilter,
    debouncedCaseSearch,
    doctorId,
    timeframe,
    chartDateSource,
    customRangeStart,
    customRangeEnd,
    refreshNonce,
  ].join("|");
  const filterKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    ReportServices.getUsers()
      .then((res) => {
        if (!cancelled) setUsersMap(res?.data?.users ?? null);
      })
      .catch(() => {
        if (!cancelled) setUsersMap(null);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshNonce]);

  const rows = useMemo(
    () =>
      rawStages.map((item) =>
        mapStageToRow(item, usersMap, commandCenterStageAction, ccStageTime),
      ),
    [rawStages, usersMap, commandCenterStageAction, ccStageTime],
  );

  useEffect(() => {
    let cancelled = false;
    HospitalServices.gethospitals("", 1, 1500)
      .then((res) => {
        if (!cancelled) setHospitalList(res?.data?.list ?? []);
      })
      .catch(() => {
        if (!cancelled) setHospitalList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshNonce]);

  const rowsWithHospitalNames = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        hospitalName: resolveHospitalName(row.hospitalId, hospitalList),
      })),
    [rows, hospitalList],
  );

  useEffect(() => {
    const prev = filterKeyRef.current;
    filterKeyRef.current = filterKey;
    if (prev !== null && prev !== filterKey && page !== 1) {
      setPage(1);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    // ✅ Get startDate & endDate (same as chart API)
    const { startDate: rawStartDate, endDate: rawEndDate } =
      chartDateSource === "custom" && customRangeStart && customRangeEnd
        ? getChartDateRangeForYmdStrings(customRangeStart, customRangeEnd)
        : getChartDateRangeForTimeframe(timeframe);

    const startDate = rawStartDate?.split("+")[0];
    const endDate = rawEndDate?.split("+")[0];

    // ✅ Build existing params
    const params = buildCcDoctorProcessedStageListParams({
      page,
      limit: PAGE_LIMIT,
      caseTypeUi: caseTypeFilter,
      caseStageUi: caseStageFilter,
      caseSearchKey: debouncedCaseSearch,
    });

    // ✅ Add new params (date + doctor conditionally)
    const payload = {
      ...params,
      startDate,
      endDate,
      ...(doctorId ? { doctorId: String(doctorId) } : {}),
    };

    // ✅ Call API with updated payload
    ReportServices.getCcDoctorProcessedStageList(payload)
      .then((res) => {
        if (cancelled) return;
        const body = res?.data ?? {};
        const list = Array.isArray(body.processedStages)
          ? body.processedStages
          : [];
        setRawStages(list);
        const tp = Number(body.totalPages);
        if (Number.isFinite(tp) && tp >= 1) {
          setTotalPages(tp);
        } else {
          const total = Number(body.total);
          if (Number.isFinite(total) && total >= 0) {
            setTotalPages(Math.max(1, Math.ceil(total / PAGE_LIMIT)));
          } else {
            setTotalPages(1);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setRawStages([]);
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load report.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    page,
    filterKey,
    timeframe,
    chartDateSource,
    customRangeStart,
    customRangeEnd,
    doctorId,
  ]);

  return (
    <div className="bg-white rounded-xl lg:rounded-[20px] overflow-hidden border border-gray-100 max-w-full">
      {error && (
        <div className="px-4 py-3 text-sm text-red-600 bg-red-50 border-b border-red-100 font-lufga">
          {error}
        </div>
      )}
      <div
        className="overflow-x-auto overflow-y-hidden max-w-full px-4"
        style={{ scrollbarGutter: "stable" }}
      >
        <div
          className="mx-auto box-border"
          style={{
            width: REPORT_TABLE_WIDTH_PX,
            minWidth: REPORT_TABLE_WIDTH_PX,
            maxWidth: REPORT_TABLE_WIDTH_PX,
          }}
        >
          <div
            className="grid min-w-0 w-full gap-x-2 text-sm font-lufga bg-white border-b border-gray-100"
            style={{ gridTemplateColumns: REPORT_GRID_TEMPLATE_COLS }}
            role="row"
          >
            <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
              Patient
            </div>
            <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
              Hospital
            </div>
            <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
              Claim ID
            </div>
            <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
              Case Stage
            </div>
            <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
              Case Type
            </div>
            <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
              Due
            </div>
            <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
              Start
            </div>
            <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
              End
            </div>
            <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
              TAT
            </div>
            <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
              Assigned
            </div>
            <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
              Action
            </div>
            <div className="box-border min-h-[36px] h-9 px-3 lg:px-4 font-semibold text-gray-900 text-xs lg:text-sm flex flex-col justify-center gap-2.5 min-w-0 w-full">
              Procedure
            </div>
            {/* <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
            Procedures Count
          </div> */}
            <div className="py-2.5 lg:py-3 px-3 lg:px-4 font-semibold text-gray-900 min-w-0 text-xs lg:text-sm">
              Status
            </div>
          </div>
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500 font-lufga">
              Loading…
            </div>
          ) : rowsWithHospitalNames.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500 font-lufga">
              No records found.
            </div>
          ) : (
            <div
              className="flex flex-col font-lufga mt-[10px]"
              style={{ gap: REPORT_ROW_GAP_PX }}
            >
              {rowsWithHospitalNames.map((row) => {
                const hospitalCell = getHospitalNameCellDisplay(
                  row.hospitalName,
                  3,
                );
                return (
                  <div
                    key={row.id}
                    className={`grid min-w-0 w-full gap-x-2 items-center min-h-[54px] rounded-[55px] transition-colors ${
                      row.actionRowColor
                        ? "hover:brightness-[0.98]"
                        : "bg-gray-50 hover:bg-gray-100/70"
                    }`}
                    style={{
                      gridTemplateColumns: REPORT_GRID_TEMPLATE_COLS,
                      ...(row.actionRowColor
                        ? styleForActionRowBackground(row.actionRowColor)
                        : {}),
                    }}
                    role="row"
                  >
                    <div className={`${REPORT_ROW_CELL_PAD}`}>
                      <div className="font-lufga font-medium text-sm leading-[100%] tracking-[-0.03em] text-gray-900">
                        {row.patientName}
                      </div>
                      <div className="font-lufga font-normal text-xs leading-[15px] tracking-[-0.5px] capitalize text-[#666666] mt-0.5">
                        {row.beneficiaryNo}
                      </div>
                    </div>
                    <div
                      className={`${REPORT_ROW_CELL_PAD} break-words text-sm ${REGULAR_CELL_CLASS} min-w-0`}
                      title={hospitalCell.title}
                    >
                      {hospitalCell.short}
                    </div>
                    <div
                      className={`${REPORT_ROW_CELL_PAD} ${REGULAR_CELL_CLASS} min-w-0 overflow-hidden text-ellipsis whitespace-nowrap`}
                      title={
                        row.claimId !== "—" ? String(row.claimId) : undefined
                      }
                    >
                      {row.claimId}
                    </div>
                    <div
                      className={`${REPORT_ROW_CELL_PAD} ${COL_CELL_CLASS} min-w-0 overflow-hidden text-ellipsis whitespace-nowrap`}
                      title={row.caseStage !== "—" ? row.caseStage : undefined}
                    >
                      {row.caseStage}
                    </div>
                    <div className={`${REPORT_ROW_CELL_PAD} ${COL_CELL_CLASS}`}>
                      {row.caseType}
                    </div>
                    <div className={REPORT_ROW_CELL_PAD}>
                      <div className={`${REGULAR_CELL_CLASS} text-center`}>
                        {row.dueTime}
                      </div>
                      {row.dueDate ? (
                        <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                          {row.dueDate}
                        </div>
                      ) : null}
                    </div>
                    <div className={REPORT_ROW_CELL_PAD}>
                      <div className={REGULAR_CELL_CLASS}>{row.startTime}</div>
                      {row.startDate ? (
                        <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                          {row.startDate}
                        </div>
                      ) : null}
                    </div>
                    <div className={REPORT_ROW_CELL_PAD}>
                      <div className={REGULAR_CELL_CLASS}>{row.endTime}</div>
                      {row.endDate ? (
                        <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                          {row.endDate}
                        </div>
                      ) : null}
                    </div>
                    <div
                      className={`${REPORT_ROW_CELL_PAD} ${REGULAR_CELL_CLASS}`}
                    >
                      {row.tat}
                    </div>
                    <div
                      className={`${REPORT_ROW_CELL_PAD} ${REGULAR_CELL_CLASS}`}
                    >
                      {row.assigned}
                    </div>
                    <div
                      className={`${REPORT_ROW_CELL_PAD} font-lufga font-medium text-sm leading-[100%] tracking-[-0.03em] text-black break-words`}
                    >
                      <div className="flex items-center gap-1">
                        <span
                          title={
                            row.action !== "—" && !row.isRejected
                              ? row.action
                              : undefined
                          }
                        >
                          {row.action}
                        </span>

                        {row.isRejected && row.rejectionReason && (
                          <RejectionInfoIcon reason={row.rejectionReason} />
                        )}
                      </div>
                    </div>
                    <div
                      className={`${REPORT_ROW_CELL_PAD} ${REGULAR_CELL_CLASS} flex flex-col gap-2.5 min-h-[36px] min-w-0 box-border justify-center`}
                    >
                      <ProcedureCell procedureNames={row.procedureNames} />
                    </div>
                    {/* Procedures Count column omitted — restore column + modal trigger from history when needed */}
                    <div className={`${REPORT_ROW_CELL_PAD} ${COL_CELL_CLASS}`}>
                      {row.status}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-center p-4 border-t border-gray-100">
        <HospitalCustomPaginaion
          page={page}
          totalPages={totalPages}
          setPage={setPage}
        />
      </div>

      {procedureListModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 font-lufga"
          role="dialog"
          aria-modal="true"
          aria-labelledby="procedure-list-modal-title"
          onClick={() => setProcedureListModal(null)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white shadow-xl flex flex-col max-h-[min(85vh,520px)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100 shrink-0">
              <div className="min-w-0 pr-2">
                <h2
                  id="procedure-list-modal-title"
                  className="text-lg font-semibold text-[#191919]"
                >
                  All procedures
                </h2>
                {procedureListModal.patientName &&
                procedureListModal.patientName !== "—" ? (
                  <p className="text-sm text-[#797979] mt-1 truncate">
                    Patient: {procedureListModal.patientName}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setProcedureListModal(null)}
                aria-label="Close"
                className="shrink-0 inline-flex h-8 w-8 items-center justify-center p-0 m-0 border-0 bg-transparent shadow-none outline-none ring-0 text-gray-400 hover:text-gray-700 text-2xl font-light leading-none cursor-pointer"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
              {procedureListModal.procedures.length > 0 ? (
                <ol className="list-decimal list-outside ml-5 space-y-3 text-sm text-[#191919] leading-relaxed">
                  {procedureListModal.procedures.map((name, i) => (
                    <li key={i} className="pl-1 break-words">
                      {name}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-gray-500">
                  No procedure names available for this case.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
